/**
 * Forgot Password component with dynamic branding
 * Handles password reset request and confirmation
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Database, Mail, Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { TraceKitConfig } from '../../types/config';
import { requestPasswordReset, confirmPasswordReset } from '../../api/auth';
import { useToastStore } from '../../stores/toast';

// Helper function to convert hex color to RGB for opacity
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'rgb(59, 130, 246)'; // Default blue
  return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
}

// Helper function to generate gradient colors from primary and secondary
function generateGradientColors(primary: string, secondary: string): {
  from: string;
  via: string;
  to: string;
  fromRgb: string;
  viaRgb: string;
  toRgb: string;
} {
  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  
  // Create intermediate color by mixing
  const primaryMatch = primaryRgb.match(/\d+/g);
  const secondaryMatch = secondaryRgb.match(/\d+/g);
  
  if (primaryMatch && secondaryMatch) {
    const viaR = Math.round((parseInt(primaryMatch[0]) + parseInt(secondaryMatch[0])) / 2);
    const viaG = Math.round((parseInt(primaryMatch[1]) + parseInt(secondaryMatch[1])) / 2);
    const viaB = Math.round((parseInt(primaryMatch[2]) + parseInt(secondaryMatch[2])) / 2);
    const viaRgb = `rgb(${viaR}, ${viaG}, ${viaB})`;
    
    return {
      from: primary,
      via: `rgb(${viaR}, ${viaG}, ${viaB})`,
      to: secondary,
      fromRgb: primaryRgb,
      viaRgb,
      toRgb: secondaryRgb,
    };
  }
  
  return {
    from: primary,
    via: primary,
    to: secondary,
    fromRgb: primaryRgb,
    viaRgb: primaryRgb,
    toRgb: secondaryRgb,
  };
}

interface ForgotPasswordProps {
  config?: TraceKitConfig;
  onBack?: () => void;
  initialToken?: string;
}

type Step = 'request' | 'success' | 'reset';

export function ForgotPassword({ config, onBack, initialToken }: ForgotPasswordProps) {
  const [step, setStep] = useState<Step>(initialToken ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(initialToken || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToastStore();

  // Check for token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam && !initialToken) {
      setToken(tokenParam);
      setStep('reset');
    }
  }, [initialToken]);

  // Extract branding values with fallbacks
  const branding = useMemo(() => {
    const brandingConfig = config?.branding || {};
    const primaryColor = brandingConfig.primaryColor || '#3B82F6'; // Default blue
    const secondaryColor = brandingConfig.secondaryColor || '#8B5CF6'; // Default purple
    
    return {
      appName: brandingConfig.appName || 'TraceKit',
      logoUrl: brandingConfig.logoUrl || brandingConfig.logo || null,
      logoAlt: brandingConfig.logoAlt || brandingConfig.appName || 'Logo',
      platformTitle: brandingConfig.platformTitle || brandingConfig.appName || 'TraceKit',
      primaryColor,
      secondaryColor,
      gradient: generateGradientColors(primaryColor, secondaryColor),
    };
  }, [config]);

  // Determine if we should show logo image or icon
  const hasLogoImage = branding.logoUrl && (branding.logoUrl.startsWith('http') || branding.logoUrl.startsWith('/'));

  // Generate dynamic background gradient style
  const backgroundGradientStyle = {
    background: `linear-gradient(to bottom right, ${branding.gradient.fromRgb}15, ${branding.gradient.viaRgb}15, ${branding.gradient.toRgb}15)`,
  };

  // Generate dynamic logo container gradient style
  const logoGradientStyle = {
    background: `linear-gradient(to bottom right, ${branding.primaryColor}, ${branding.secondaryColor})`,
    boxShadow: `0 20px 25px -5px ${branding.primaryColor}50, 0 10px 10px -5px ${branding.primaryColor}30`,
  };

  // Generate dynamic title gradient style
  const titleGradientStyle = {
    background: `linear-gradient(to right, #0f172a, ${branding.primaryColor}, ${branding.secondaryColor})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  // Validate password
  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const passwordErrors = newPassword ? validatePassword(newPassword) : [];
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  // Handle password reset request
  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    try {
      await requestPasswordReset({ email: email.trim() });
      setStep('success');
      addToast({
        message: 'If an account exists with this email, a password reset link has been sent.',
        type: 'success',
      });
    } catch (error: any) {
      addToast({
        message: error.message || 'Failed to send password reset email. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset confirmation
  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordErrors.length > 0) {
      addToast({
        message: 'Please fix password requirements',
        type: 'error',
      });
      return;
    }

    if (!passwordsMatch) {
      addToast({
        message: 'Passwords do not match',
        type: 'error',
      });
      return;
    }

    if (!token.trim()) {
      addToast({
        message: 'Reset token is required',
        type: 'error',
      });
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset({
        token: token.trim(),
        new_password: newPassword,
      });
      addToast({
        message: 'Password reset successful! Please log in with your new password.',
        type: 'success',
      });
      // Navigate back to login after a short delay
      setTimeout(() => {
        if (onBack) {
          onBack();
        } else {
          window.location.href = window.location.pathname.split('?')[0];
        }
      }, 2000);
    } catch (error: any) {
      addToast({
        message: error.message || 'Failed to reset password. The token may be invalid or expired.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render request step
  if (step === 'request') {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={backgroundGradientStyle}
      >
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        
        <div className="w-full max-w-md space-y-8 animate-slide-in">
          {/* Logo and header */}
          <div className="text-center">
            <div 
              className="inline-flex p-4 rounded-2xl mb-6"
              style={logoGradientStyle}
            >
              {hasLogoImage ? (
                <img 
                  src={branding.logoUrl!} 
                  alt={branding.logoAlt}
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Database className="h-12 w-12 text-white" />
              )}
            </div>
            <h1 
              className="text-4xl font-bold bg-clip-text text-transparent"
              style={titleGradientStyle}
            >
              Reset Password
            </h1>
            <p className="mt-3 text-lg text-slate-600 font-medium">
              Enter your email to receive a password reset link
            </p>
          </div>

          {/* Request card */}
          <div className="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-slate-200/50">
            <form onSubmit={handleRequestReset} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  leftIcon={<Mail className="h-5 w-5 text-slate-400" />}
                  className="h-12 text-base"
                  autoComplete="email"
                  required
                />
                <p className="mt-2 text-xs text-slate-500">
                  We'll send you a link to reset your password
                </p>
              </div>

              <Button
                type="submit"
                disabled={!email.trim() || isLoading}
                className="w-full h-12 text-base font-semibold transition-all transform hover:scale-[1.02] text-white"
                style={{
                  background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                  boxShadow: `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}dd, ${branding.secondaryColor}dd)`;
                  e.currentTarget.style.boxShadow = `0 20px 25px -5px ${branding.primaryColor}40, 0 10px 10px -5px ${branding.primaryColor}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`;
                  e.currentTarget.style.boxShadow = `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`;
                }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-5 w-5" />
                    Send Reset Link
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={onBack}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                style={{ color: branding.primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = branding.secondaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = branding.primaryColor;
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render success step
  if (step === 'success') {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={backgroundGradientStyle}
      >
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        
        <div className="w-full max-w-md space-y-8 animate-slide-in">
          <div className="text-center">
            <div 
              className="inline-flex p-4 rounded-2xl mb-6"
              style={logoGradientStyle}
            >
              {hasLogoImage ? (
                <img 
                  src={branding.logoUrl!} 
                  alt={branding.logoAlt}
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Database className="h-12 w-12 text-white" />
              )}
            </div>
            <div className="inline-flex p-4 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 
              className="text-4xl font-bold bg-clip-text text-transparent"
              style={titleGradientStyle}
            >
              Check Your Email
            </h1>
            <p className="mt-3 text-lg text-slate-600 font-medium">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Click the link in the email to reset your password. The link will expire in 1 hour.
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-slate-200/50">
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Didn't receive the email?</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Check your spam or junk folder</li>
                      <li>Make sure you entered the correct email address</li>
                      <li>Wait a few minutes and try again</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setStep('request')}
                className="w-full h-12 text-base font-semibold transition-all transform hover:scale-[1.02] text-white"
                style={{
                  background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                  boxShadow: `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}dd, ${branding.secondaryColor}dd)`;
                  e.currentTarget.style.boxShadow = `0 20px 25px -5px ${branding.primaryColor}40, 0 10px 10px -5px ${branding.primaryColor}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`;
                  e.currentTarget.style.boxShadow = `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`;
                }}
              >
                Resend Email
              </Button>

              <button
                type="button"
                onClick={onBack}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors"
                style={{ color: branding.primaryColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = branding.secondaryColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = branding.primaryColor;
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render reset step (with token)
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={backgroundGradientStyle}
    >
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
      
      <div className="w-full max-w-md space-y-8 animate-slide-in">
        {/* Logo and header */}
        <div className="text-center">
          <div 
            className="inline-flex p-4 rounded-2xl mb-6"
            style={logoGradientStyle}
          >
            {hasLogoImage ? (
              <img 
                src={branding.logoUrl!} 
                alt={branding.logoAlt}
                className="h-12 w-12 object-contain"
              />
            ) : (
              <Database className="h-12 w-12 text-white" />
            )}
          </div>
          <h1 
            className="text-4xl font-bold bg-clip-text text-transparent"
            style={titleGradientStyle}
          >
            Set New Password
          </h1>
          <p className="mt-3 text-lg text-slate-600 font-medium">
            Enter your new password below
          </p>
        </div>

        {/* Reset card */}
        <div className="bg-white/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-slate-200/50">
          <form onSubmit={handleConfirmReset} className="space-y-5">
            {!initialToken && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Reset Token
                </label>
                <Input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter reset token from email"
                  leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                  className="h-12 text-base"
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  Check your email for the reset token
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                New Password *
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  }
                  className="h-12 text-base pr-12"
                  autoComplete="new-password"
                  required
                />
              </div>
              {newPassword && (
                <div className="mt-2 space-y-1">
                  {[
                    { check: newPassword.length >= 12, text: 'At least 12 characters' },
                    { check: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
                    { check: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
                    { check: /[0-9]/.test(newPassword), text: 'One number' },
                    { check: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword), text: 'One special character' },
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      {req.check ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
                      )}
                      <span className={req.check ? 'text-green-600' : 'text-slate-500'}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Confirm New Password *
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  leftIcon={<Lock className="h-5 w-5 text-slate-400" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-slate-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                  }
                  className="h-12 text-base pr-12"
                  autoComplete="new-password"
                  required
                />
              </div>
              {confirmPassword && (
                <div className="mt-2">
                  {passwordsMatch ? (
                    <div className="flex items-center gap-2 text-xs text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      Passwords match
                    </div>
                  ) : (
                    <div className="text-xs text-red-600">
                      Passwords do not match
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={
                !token.trim() ||
                !newPassword || 
                !confirmPassword ||
                passwordErrors.length > 0 ||
                !passwordsMatch ||
                isLoading
              }
              className="w-full h-12 text-base font-semibold transition-all transform hover:scale-[1.02] text-white"
              style={{
                background: `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`,
                boxShadow: `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}dd, ${branding.secondaryColor}dd)`;
                e.currentTarget.style.boxShadow = `0 20px 25px -5px ${branding.primaryColor}40, 0 10px 10px -5px ${branding.primaryColor}30`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(to right, ${branding.primaryColor}, ${branding.secondaryColor})`;
                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${branding.primaryColor}30, 0 4px 6px -2px ${branding.primaryColor}20`;
              }}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Resetting Password...
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5" />
                  Reset Password
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onBack}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              style={{ color: branding.primaryColor }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = branding.secondaryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = branding.primaryColor;
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
